import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ApiError } from "../lib/api";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Pencil,
  Sparkles,
  X,
} from "lucide-react";
import {
  suggestHqRule,
  useAnalyzeWebhookEndpoint,
  useReplaySample,
  useUpdateWebhookEndpoint,
  useWebhookEndpoint,
  useWebhookEndpoints,
  type FilterOp,
  type MappingFilterRule,
  type WebhookMapping,
  type WebhookSample,
} from "../lib/webhook-endpoints";
import { ProjectPausedWebhookBanner, TableSkeleton } from "../components/states";
import { useProjectDetail } from "../lib/project-detail";
import {
  digistoreShouldTrack,
  digistoreValues,
  isDigistoreTestPurchase,
} from "../lib/digistore-preview";
import { resolvePath } from "../lib/webhook-path";
import { keepHqRule, mappingWithHqRule } from "../lib/keep-hq-rule";
import { hqMetaOnly, readHqRule, type HqRule } from "../lib/hq-rule";
import { hqMetaSummary } from "../lib/hq-meta-hint";
import { useTrackingConfig } from "../lib/project-tracking";
import { HqRuleEditor } from "../components/HqRuleEditor";
import { ablefyShouldTrack, ablefyValues } from "../lib/ablefy-preview";
import { SettingRow, SettingsPanel } from "../components/SettingRow";
import { CountRuleRetroDialog } from "../components/CountRuleRetroDialog";
import {
  COUNT_RULE_OPTIONS,
  countRuleChanged,
  countRuleResyncRunLabel,
  shouldShowPurchaseHint,
  validateCountIntervalDaysInput,
  type CountRule,
} from "../lib/count-rule";

/**
 * TASK-190 review findings C/D — the retroactive-resync save can now come
 * back 409 with a specific reason instead of just failing generically.
 */
function countRuleSaveErrorMessage(err: unknown): string {
  const code = err instanceof ApiError ? (err.body as { error?: string } | undefined)?.error : undefined;
  if (code === "resync_already_running") {
    return "Speichern fehlgeschlagen: Für diese Quelle läuft bereits ein rückwirkendes Angleichen — bitte warten, bis es fertig ist.";
  }
  if (code === "resync_catchup_pending") {
    return "Der Nachzug des letzten Laufs ist noch nicht fertig — bitte später erneut versuchen.";
  }
  return `Speichern fehlgeschlagen: ${(err as Error).message}`;
}

/**
 * Wizard-style detail page for a single webhook-endpoint. Shows the public
 * URL to copy, the live sample stream while detecting, an AI-suggested
 * field mapping the user can edit row-by-row, and the activate button.
 */
export function WebhookEndpointDetailPage() {
  const { id: projectIdStr, endpointId: endpointIdStr } = useParams();
  const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;
  const endpointId = endpointIdStr ? parseInt(endpointIdStr, 10) : null;
  const query = useWebhookEndpoint(projectId, endpointId);
  const projectQuery = useProjectDetail(projectId ?? 0);
  const update = useUpdateWebhookEndpoint(projectId ?? 0, endpointId ?? 0);
  const analyze = useAnalyzeWebhookEndpoint(projectId ?? 0, endpointId ?? 0);
  const replay = useReplaySample(projectId ?? 0, endpointId ?? 0);
  const [draftMapping, setDraftMapping] = useState<WebhookMapping | null>(null);
  const [analysisNote, setAnalysisNote] = useState<string | null>(null);
  const [analysisSource, setAnalysisSource] = useState<string | null>(null);
  // IDs of samples the operator hand-picked for the LLM. Empty = use all
  // (latest 5) — same as the old single-sample behavior.
  const [selectedSampleIds, setSelectedSampleIds] = useState<Set<number>>(
    new Set(),
  );
  // Custom-prompt override for the LLM analyzer (always Claude Fable 5).
  const [showPromptOverride, setShowPromptOverride] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  // "Payload identisch wie bei …": borrow another endpoint's samples as
  // examples for analysis + mapping preview. Pure UI state — nothing is
  // copied; clearing the dropdown fully reverts to the endpoint's own samples.
  const [borrowFromId, setBorrowFromId] = useState<number | null>(null);
  const endpointsList = useWebhookEndpoints(projectId);
  const borrowQuery = useWebhookEndpoint(projectId, borrowFromId);
  const [draftCountRule, setDraftCountRule] = useState<CountRule | null>(null);
  const [draftCountIntervalDays, setDraftCountIntervalDays] = useState("");
  const [showRetroDialog, setShowRetroDialog] = useState(false);
  const trackingConfig = useTrackingConfig(projectId ?? 0);

  const toggleSample = (id: number) => {
    setSelectedSampleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (query.data?.endpoint.mapping_json) {
      setDraftMapping(query.data.endpoint.mapping_json);
    }
  }, [query.data?.endpoint.id]);

  useEffect(() => {
    if (query.data?.endpoint) {
      setDraftCountRule(query.data.endpoint.count_rule);
      setDraftCountIntervalDays(String(query.data.endpoint.count_interval_days ?? 30));
    }
  }, [query.data?.endpoint.id]);

  if (!projectId || !endpointId) return null;

  const endpoint = query.data?.endpoint;
  const lastRun = query.data?.lastRun ?? null;
  const ownSamples = query.data?.samples ?? [];
  // Borrowed samples replace the own list for display, analysis and preview.
  const samples = borrowFromId ? (borrowQuery.data?.samples ?? []) : ownSamples;
  const otherEndpoints = (endpointsList.data?.items ?? []).filter(
    (e) => e.id !== endpointId,
  );
  const borrowedFrom = borrowFromId
    ? otherEndpoints.find((e) => e.id === borrowFromId)
    : undefined;

  const setBorrow = (id: number | null) => {
    setBorrowFromId(id);
    setSelectedSampleIds(new Set()); // selection refers to the visible list
  };

  const publicUrl = endpoint
    ? `${window.location.origin.replace("app.", "api.")}/webhook/in/${endpoint.slug}`
    : "";

  const runAnalyze = () => {
    const picked = [...selectedSampleIds];
    const input: {
      sampleIds?: number[];
      customPrompt?: string;
      sampleEndpointId?: number;
    } = {};
    if (picked.length > 0) input.sampleIds = picked;
    if (showPromptOverride && customPrompt.trim()) {
      input.customPrompt = customPrompt;
    }
    if (borrowFromId) input.sampleEndpointId = borrowFromId;
    analyze.mutate(input, {
      onSuccess: (res) => {
        setDraftMapping((prev) => keepHqRule(res.mapping, prev));
        setAnalysisNote(res.notes);
        setAnalysisSource(res.source);
        const sampleHint =
          res.samplesUsed === 1 ? "1 Sample" : `${res.samplesUsed} Samples`;
        toast.success(
          res.source === "preset"
            ? res.mapping.preset === "ablefy"
              ? "Ablefy erkannt — Special Integration aktiviert"
              : "Digistore24 erkannt — Special Integration aktiviert"
            : res.source === "llm"
              ? `Claude Fable 5 hat ein Mapping aus ${sampleHint} vorgeschlagen`
              : `Vorschlag aus ${res.source} (${sampleHint})`,
        );
      },
      onError: (err) =>
        toast.error(`Analyse fehlgeschlagen: ${(err as Error).message}`),
    });
  };

  const replaySample = (sampleId: number) => {
    if (!draftMapping || !endpoint?.mapping_json) {
      toast.error(
        "Mapping muss vorher gespeichert sein — sonst kann der Sample nicht durchprojeziert werden.",
      );
      return;
    }
    replay.mutate(sampleId, {
      onSuccess: (res) =>
        toast.success(`Sample wurde als Event ${res.event_id.slice(0, 8)}… in die Pipeline geschrieben`),
      onError: (err) =>
        toast.error(
          (err as Error).message.includes("sample_filtered_by_only_track_if")
            ? "Sample wird vom „Nur tracken wenn“-Filter verworfen — kein Event erzeugt."
            : `Replay fehlgeschlagen: ${(err as Error).message}`,
        ),
    });
  };

  // TASK-234: the PATCH replaces mapping_json as a whole, so the stored
  // mapping goes along with the new hq_rule. The draft keeps the new rule too,
  // else "Mapping speichern" would write the old one back.
  const saveHqRule = async (rule: HqRule) => {
    const stored = endpoint?.mapping_json;
    if (!stored) throw new Error("no stored mapping");
    await update.mutateAsync({ mappingJson: mappingWithHqRule(stored, rule) });
    setDraftMapping((prev) => (prev ? mappingWithHqRule(prev, rule) : prev));
    toast.success("HQ-Regel gespeichert");
  };

  const saveMapping = () => {
    if (!draftMapping) return;
    update.mutate(
      { mappingJson: draftMapping },
      {
        onSuccess: () => toast.success("Mapping gespeichert"),
        onError: (err) =>
          toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  const activate = () => {
    if (!draftMapping) {
      toast.error("Erst Mapping vorschlagen + speichern");
      return;
    }
    update.mutate(
      { status: "active", mappingJson: draftMapping },
      {
        onSuccess: () => toast.success("Endpoint aktiviert"),
        onError: (err) =>
          toast.error(`Aktivieren fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  const pause = () => {
    update.mutate(
      { status: "disabled" },
      {
        onSuccess: () => toast.success("Endpoint pausiert"),
        onError: (err) =>
          toast.error(`Pausieren fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  const reEnableDetect = () => {
    update.mutate(
      { status: "detecting" },
      {
        onSuccess: () => toast.success("Endpoint hört wieder auf neue Samples"),
      },
    );
  };

  const countRule = draftCountRule ?? "every";
  const daysValidation = validateCountIntervalDaysInput(draftCountIntervalDays, countRule);
  const countRuleDirty =
    endpoint !== undefined &&
    (countRule !== endpoint.count_rule ||
      (daysValidation.ok && daysValidation.value !== endpoint.count_interval_days));

  const submitCountRule = (retroactive: boolean) => {
    if (!daysValidation.ok) return;
    update.mutate(
      { countRule, countIntervalDays: daysValidation.value, retroactive },
      {
        onSuccess: () =>
          toast.success(
            retroactive ? "Zählregel gespeichert — Angleichen läuft" : "Zählregel gespeichert",
          ),
        onError: (err) => toast.error(countRuleSaveErrorMessage(err)),
      },
    );
  };

  // S8-1/S8-5: the dialog only guards an actual change on an EXISTING
  // source — this page never renders for a brand-new one (create lives on
  // a separate form that never calls this).
  const saveCountRule = () => {
    if (!daysValidation.ok || !endpoint) return;
    if (
      countRuleChanged(
        { rule: endpoint.count_rule, intervalDays: endpoint.count_interval_days },
        { rule: countRule, intervalDays: daysValidation.value },
      )
    ) {
      setShowRetroDialog(true);
      return;
    }
    submitCountRule(false);
  };

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <Link
          to={`/projects/${projectId}/integrations`}
          className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft size={12} /> Integrations
        </Link>

        {query.isLoading || !endpoint ? (
          <TableSkeleton rows={3} />
        ) : (
          <>
            {projectQuery.data?.active === false && (
              <ProjectPausedWebhookBanner />
            )}
            <header className="flex items-baseline justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Webhook-Endpoint
                </p>
                <EditableName
                  name={endpoint.name}
                  onSave={(name) =>
                    update.mutate(
                      { name },
                      {
                        onSuccess: () => toast.success("Name geändert"),
                        onError: (err) =>
                          toast.error(
                            `Umbenennen fehlgeschlagen: ${(err as Error).message}`,
                          ),
                      },
                    )
                  }
                />
                <EditableEventType
                  eventType={endpoint.event_type_default}
                  onSave={(eventType) =>
                    update.mutate(
                      { eventType },
                      {
                        onSuccess: () =>
                          toast.success(
                            "Event-Typ geändert — gilt für künftig eingehende Events",
                          ),
                        onError: (err) =>
                          toast.error(
                            `Ändern fehlgeschlagen: ${(err as Error).message}`,
                          ),
                      },
                    )
                  }
                />
              </div>
              <StatusBadge status={endpoint.status} />
            </header>

            <section className="rounded-md border border-border-primary bg-bg-secondary p-4">
              <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                URL zum Eintragen in dein Quellsystem
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate font-mono text-xs text-text-primary">
                  {publicUrl}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(publicUrl);
                    toast.success("URL kopiert");
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
                >
                  <Copy size={12} /> Kopieren
                </button>
              </div>
              <p className="mt-2 text-xs text-text-secondary">
                Trage diese URL als Webhook-Ziel in deinem CRM/Webinar-Tool/
                Checkout ein. Erste Requests werden als Samples gespeichert
                — keine Events werden generiert bis das Mapping aktiviert ist.
              </p>
            </section>

            <section className="rounded-md border border-border-primary bg-bg-secondary p-4">
              <div className="mb-1 flex items-center justify-between">
                <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Samples ({samples.length})
                  {borrowedFrom && (
                    <span className="ml-2 normal-case tracking-normal text-accent-text">
                      Beispiele von »{borrowedFrom.name}«
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={runAnalyze}
                    disabled={analyze.isPending || samples.length === 0}
                    className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
                  >
                    <Sparkles size={14} />
                    {analyze.isPending
                      ? "Analysiere…"
                      : selectedSampleIds.size > 0
                        ? `Analysiere ${selectedSampleIds.size} ausgewählt${selectedSampleIds.size === 1 ? "es" : "e"}`
                        : draftMapping
                          ? "Mapping neu vorschlagen"
                          : "Mit KI analysieren"}
                  </button>
                </div>
              </div>

              {otherEndpoints.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <label className="text-text-secondary" htmlFor="borrow-samples">
                    Payload identisch wie bei …
                  </label>
                  <select
                    id="borrow-samples"
                    value={borrowFromId ?? ""}
                    onChange={(e) =>
                      setBorrow(e.target.value ? Number(e.target.value) : null)
                    }
                    className="rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none"
                  >
                    <option value="">— eigene Samples verwenden —</option>
                    {otherEndpoints.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name} ({e.event_type_default})
                      </option>
                    ))}
                  </select>
                  {borrowFromId && (
                    <button
                      type="button"
                      onClick={() => setBorrow(null)}
                      className="inline-flex items-center gap-1 rounded-md border border-border-primary px-2 py-1 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                    >
                      <X size={11} /> Entfernen
                    </button>
                  )}
                  {borrowFromId && (
                    <span className="w-full text-[11px] text-text-tertiary">
                      Analyse &amp; Mapping-Vorschau nutzen die Samples dieses
                      Endpoints als Beispiele. Es wird nichts kopiert — „Entfernen"
                      stellt die eigenen Samples wieder her.
                    </span>
                  )}
                </div>
              )}

              <details
                open={showPromptOverride}
                onToggle={(e) =>
                  setShowPromptOverride((e.target as HTMLDetailsElement).open)
                }
                className="mb-3 mt-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-xs"
              >
                <summary className="cursor-pointer text-text-secondary">
                  Custom-Prompt (für tricky Payloads — wenn die Default-
                  Heuristik daneben liegt)
                </summary>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder={`Eigene Anweisung an die KI. Beispiel:\n\nDie Payloads stammen aus EverWebinar. Die Email steckt immer in lead.email, das Telefon in lead.phone_number — ignoriere alle anderen Email-artigen Felder. Wenn registrant_role = "presenter" ist, NICHT als Lead mappen.`}
                  rows={6}
                  className="mt-2 w-full rounded-md border border-border-primary bg-bg-tertiary px-2 py-1.5 font-mono text-[11px] text-text-primary"
                />
                <p className="mt-1 text-[10px] text-text-tertiary">
                  Wird statt der Default-Anweisung an Claude geschickt. Das
                  erwartete JSON-Antwort-Schema hängen wir automatisch an.
                  Leer lassen = Default-Prompt benutzen.
                </p>
              </details>
              <p className="mb-3 text-xs text-text-tertiary">
                Häkchen setzen, um nur bestimmte Samples in die KI-Analyse zu
                schicken (max 5 werden verwendet). Ohne Auswahl werden die
                letzten 5 Samples genommen.
                {selectedSampleIds.size > 0 && (
                  <>
                    {" "}
                    <button
                      type="button"
                      onClick={() => setSelectedSampleIds(new Set())}
                      className="text-text-link hover:text-text-link-hover"
                    >
                      Auswahl löschen
                    </button>
                  </>
                )}
              </p>
              {samples.length === 0 ? (
                <p className="text-xs text-text-tertiary">
                  {borrowFromId
                    ? "Der ausgewählte Endpoint hat selbst noch keine Samples."
                    : "Noch keine Requests empfangen. Schicke einen Test-Request an obige URL — er erscheint hier in <5 s."}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {samples.slice(0, 30).map((s) => (
                    <SampleRow
                      key={s.id}
                      sample={s}
                      selected={selectedSampleIds.has(s.id)}
                      onToggle={() => toggleSample(s.id)}
                      canReplay={Boolean(endpoint?.mapping_json) && !borrowFromId}
                      onReplay={() => replaySample(s.id)}
                      replaying={replay.isPending}
                      testPurchase={
                        draftMapping?.preset === "digistore24"
                          ? isDigistoreTestPurchase(s.payload)
                          : undefined
                      }
                    />
                  ))}
                </ul>
              )}
              {analysisNote && (
                <p className="mt-3 rounded-md bg-bg-tertiary px-2 py-1 text-[11px] text-text-secondary">
                  {analysisSource === "llm" ? "🤖" : "⚙️"} {analysisNote}
                </p>
              )}
            </section>

            {draftMapping && samples.length > 0 && (
              <MappingEditor
                mapping={draftMapping}
                onChange={setDraftMapping}
                samples={samples.map((s) => s.payload)}
              />
            )}

            {endpoint.mapping_json && (
              <HqRuleEditor
                saved={readHqRule(endpoint.mapping_json.hq_rule)}
                samples={samples.map((s) => s.payload)}
                onSave={saveHqRule}
                onSuggest={(sentence) => suggestHqRule(projectId ?? 0, endpointId ?? 0, sentence)}
                busy={update.isPending || query.isFetching}
                metaSummary={hqMetaSummary(
                  trackingConfig.data?.config.meta_event_allowlist ?? null,
                  trackingConfig.isError ? "error" : trackingConfig.data ? "ready" : "loading",
                  hqMetaOnly(endpoint.mapping_json.hq_rule),
                )}
              />
            )}

            <section className="flex flex-wrap gap-2">
              {draftMapping && (
                <button
                  type="button"
                  onClick={saveMapping}
                  disabled={update.isPending}
                  className="rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-hover disabled:opacity-50"
                >
                  Mapping speichern
                </button>
              )}
              {endpoint.status !== "active" && draftMapping && (
                <button
                  type="button"
                  onClick={activate}
                  disabled={update.isPending}
                  className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
                >
                  Endpoint aktivieren
                </button>
              )}
              {endpoint.status === "active" && (
                <button
                  type="button"
                  onClick={pause}
                  disabled={update.isPending}
                  className="rounded-md border border-danger bg-bg-secondary px-3 py-1.5 text-sm font-medium text-danger hover:bg-danger-bg disabled:opacity-50"
                >
                  Pausieren
                </button>
              )}
              {endpoint.status === "disabled" && (
                <button
                  type="button"
                  onClick={reEnableDetect}
                  disabled={update.isPending}
                  className="rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-hover disabled:opacity-50"
                >
                  Wieder auf detecting
                </button>
              )}
            </section>

            <SettingsPanel>
              <SettingRow
                title="Zählregel"
                description="Legt fest, welche eingehenden Events als echte Conversion zählen. Wirkt sofort auf neu eingehende Events — eine Wiederholung wird als „<Name>-Repeat“ gespeichert und nicht an Werbeziele gesendet."
              >
                <div className="flex flex-wrap items-end gap-2">
                  <select
                    value={countRule}
                    onChange={(e) => setDraftCountRule(e.target.value as CountRule)}
                    className="h-9 rounded-md border border-border-primary bg-bg-primary px-2 text-sm text-text-primary focus:border-border-focus focus:outline-none"
                  >
                    {COUNT_RULE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {countRule === "interval" && (
                    <div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={draftCountIntervalDays}
                        onChange={(e) => setDraftCountIntervalDays(e.target.value)}
                        className="h-9 w-20 rounded-md border border-border-primary bg-bg-primary px-2 text-sm text-text-primary focus:border-border-focus focus:outline-none"
                      />
                      {!daysValidation.ok && (
                        <p className="mt-1 text-[11px] text-danger">{daysValidation.message}</p>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={saveCountRule}
                    disabled={!countRuleDirty || !daysValidation.ok || update.isPending}
                    className="h-9 rounded-md bg-accent px-3 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-60"
                  >
                    Speichern
                  </button>
                </div>
                {shouldShowPurchaseHint(endpoint.event_type_default, countRule) && (
                  <p className="mt-2 rounded-md bg-warning-bg px-3 py-1.5 text-xs text-warning">
                    Wiederholungskäufe zählen dann nicht als Umsatz.
                  </p>
                )}
                {lastRun && (
                  <p className="mt-2 text-xs text-text-secondary">
                    Letztes rückwirkendes Angleichen: {countRuleResyncRunLabel(lastRun)}
                  </p>
                )}
              </SettingRow>
            </SettingsPanel>
          </>
        )}
      </div>
      <CountRuleRetroDialog
        open={showRetroDialog}
        onOpenChange={setShowRetroDialog}
        onSaveOnly={() => {
          setShowRetroDialog(false);
          submitCountRule(false);
        }}
        onSaveRetroactive={() => {
          setShowRetroDialog(false);
          submitCountRule(true);
        }}
      />
    </div>
  );
}

// Click-to-edit endpoint name: h1 → input, Enter/blur saves, Escape cancels.
function EditableName({
  name,
  onSave,
}: {
  name: string;
  onSave: (name: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  if (draft === null) {
    return (
      <h1
        onClick={() => setDraft(name)}
        title="Klicken zum Umbenennen"
        className="group mt-1 flex cursor-pointer items-center gap-2 text-2xl font-semibold tracking-tight text-text-primary hover:text-text-link"
      >
        {name}
        <Pencil
          size={14}
          className="shrink-0 text-text-tertiary group-hover:text-text-link"
        />
      </h1>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    setDraft(null);
    if (trimmed && trimmed !== name) onSave(trimmed);
  };

  return (
    <input
      type="text"
      value={draft}
      autoFocus
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setDraft(null);
      }}
      className="mt-1 w-full rounded-md border border-border-primary bg-bg-primary px-2 py-0.5 text-2xl font-semibold tracking-tight text-text-primary focus:border-accent focus:outline-none"
    />
  );
}

// Same click-to-edit pattern as EditableName, styled to match the small
// mono meta line. Changing the type only affects future incoming events —
// already-ingested events keep the type they were stored with.
function EditableEventType({
  eventType,
  onSave,
}: {
  eventType: string;
  onSave: (eventType: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  if (draft === null) {
    return (
      <p
        onClick={() => setDraft(eventType)}
        title="Klicken zum Ändern — gilt für künftig eingehende Events"
        className="group mt-0.5 flex cursor-pointer items-center gap-1.5 font-mono text-xs text-text-tertiary hover:text-text-link"
      >
        Event-Typ: {eventType}
        <Pencil
          size={11}
          className="shrink-0 text-text-tertiary group-hover:text-text-link"
        />
      </p>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    setDraft(null);
    if (trimmed && trimmed !== eventType) onSave(trimmed);
  };

  return (
    <div className="mt-0.5 flex items-center gap-1.5 font-mono text-xs text-text-tertiary">
      Event-Typ:
      <input
        type="text"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setDraft(null);
        }}
        placeholder="CompleteRegistration / Schedule / Purchase …"
        className="w-64 rounded-md border border-border-primary bg-bg-primary px-1.5 py-0.5 font-mono text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
      />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    detecting: { label: "DETECTING", cls: "bg-warning-bg text-warning" },
    active: { label: "AKTIV", cls: "bg-success/20 text-success" },
    disabled: { label: "PAUSIERT", cls: "bg-bg-tertiary text-text-tertiary" },
  };
  const cfg = map[status] ?? map["detecting"]!;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function SampleRow({
  sample,
  selected,
  onToggle,
  canReplay,
  onReplay,
  replaying,
  testPurchase,
}: {
  sample: WebhookSample;
  selected: boolean;
  onToggle: () => void;
  canReplay: boolean;
  onReplay: () => void;
  replaying: boolean;
  /** TASK-85: set only for digistore24-preset endpoints — undefined elsewhere. */
  testPurchase?: { isTest: boolean; reason: string };
}) {
  const [open, setOpen] = useState(false);
  return (
    <li
      className={`rounded-md border bg-bg-primary transition-colors ${
        selected ? "border-accent" : "border-border-primary"
      }`}
    >
      <div className="flex w-full items-center gap-2 px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 cursor-pointer"
          aria-label={
            selected
              ? "Sample aus Auswahl entfernen"
              : "Sample für KI-Analyse auswählen"
          }
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-1 items-center gap-2 text-left hover:opacity-80"
        >
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <span className="flex-1 truncate font-mono text-[11px] text-text-secondary">
            {Object.keys((sample.payload as Record<string, unknown>) ?? {})
              .slice(0, 4)
              .join(", ") || "(leerer Payload)"}
          </span>
          <span className="font-mono text-[10px] text-text-tertiary">
            {new Date(sample.received_at).toLocaleString("de-DE")}
          </span>
        </button>
        {testPurchase?.isTest && (
          <span
            title={`Wird beim Empfang automatisch übersprungen — ${testPurchase.reason}`}
            className="flex-shrink-0 whitespace-nowrap rounded-full bg-warning-bg px-2 py-0.5 font-mono text-[10px] font-semibold text-warning"
          >
            Testkauf — {testPurchase.reason}
          </span>
        )}
        <button
          type="button"
          onClick={onReplay}
          disabled={!canReplay || replaying}
          title={
            canReplay
              ? "Sample durch das gespeicherte Mapping in die Pipeline schicken (created_at = received_at, also rückdatiert)"
              : "Erst Mapping speichern, dann kann dieser Sample replayed werden"
          }
          className="flex-shrink-0 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-[11px] text-text-secondary hover:bg-bg-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          {replaying ? "…" : "An Pipeline"}
        </button>
      </div>
      {open && (
        <pre className="max-h-[40vh] overflow-auto border-t border-border-primary bg-bg-tertiary p-3 font-mono text-[11px] leading-relaxed text-text-primary">
          {JSON.stringify(sample.payload, null, 2)}
        </pre>
      )}
    </li>
  );
}

function MappingEditor({
  mapping,
  onChange,
  samples,
}: {
  mapping: WebhookMapping;
  onChange: (next: WebhookMapping) => void;
  samples: unknown[];
}) {
  const setBucket = (
    bucket: "keys" | "fields" | "custom_data",
    next: Record<string, string>,
  ) => {
    onChange({ ...mapping, [bucket]: next });
  };
  // All leaf paths across the samples with an example value — feeds the
  // "nächstgelegene Alternativen" dropdown in every path input.
  const flatPaths = useMemo(() => flattenSamples(samples), [samples]);
  if (mapping.preset === "digistore24") {
    return (
      <section className="rounded-md border border-border-primary bg-bg-secondary p-4">
        <p className="mb-3 font-mono text-xs uppercase tracking-wider text-text-tertiary">
          Mapping
        </p>
        <DigistorePanel
          samples={samples}
          onRemovePreset={() => {
            const next = { ...mapping };
            delete next.preset;
            onChange(next);
          }}
        />
        <FilterEditor
          filter={mapping.filter}
          onChange={(filter) => {
            const next = { ...mapping };
            if (filter) next.filter = filter;
            else delete next.filter;
            onChange(next);
          }}
          samples={samples}
          flatPaths={flatPaths}
        />
      </section>
    );
  }
  if (mapping.preset === "ablefy") {
    return (
      <section className="rounded-md border border-border-primary bg-bg-secondary p-4">
        <p className="mb-3 font-mono text-xs uppercase tracking-wider text-text-tertiary">
          Mapping
        </p>
        <AblefyPanel
          samples={samples}
          onRemovePreset={() => {
            const next = { ...mapping };
            delete next.preset;
            onChange(next);
          }}
        />
        <FilterEditor
          filter={mapping.filter}
          onChange={(filter) => {
            const next = { ...mapping };
            if (filter) next.filter = filter;
            else delete next.filter;
            onChange(next);
          }}
          samples={samples}
          flatPaths={flatPaths}
        />
      </section>
    );
  }
  return (
    <section className="rounded-md border border-border-primary bg-bg-secondary p-4">
      <p className="mb-3 font-mono text-xs uppercase tracking-wider text-text-tertiary">
        Mapping
      </p>
      <p className="mb-3 text-xs text-text-secondary">
        Pfadangaben in gepunktet-Notation, z.B.{" "}
        <code className="rounded bg-bg-tertiary px-1 font-mono text-[10px]">
          data.lead.email
        </code>
        . Die Spalte rechts zeigt was beim aktuellsten Sample mit Wert
        herauskommt (probiert alle Samples nacheinander).
      </p>
      <BucketEditor
        title="Identity-Keys"
        bucket={mapping.keys ?? {}}
        setBucket={(b) => setBucket("keys", b)}
        samples={samples}
        flatPaths={flatPaths}
        suggestedTargets={[
          "email",
          "phone",
          "client_id",
          "fbclid",
          "gclid",
          "fbp",
          "fbc",
        ]}
      />
      <BucketEditor
        title="Event-Felder"
        bucket={mapping.fields ?? {}}
        setBucket={(b) => setBucket("fields", b)}
        samples={samples}
        flatPaths={flatPaths}
        suggestedTargets={[
          "deal_value",
          "deal_currency",
          "order_id",
          "lead_first_name",
          "lead_last_name",
          "lead_company",
          "lead_country",
          "event_type",
        ]}
      />
      <BucketEditor
        title="Custom Data / Attribution"
        bucket={mapping.custom_data ?? {}}
        setBucket={(b) => setBucket("custom_data", b)}
        samples={samples}
        flatPaths={flatPaths}
        suggestedTargets={[
          "utm_source",
          "utm_medium",
          "utm_campaign",
          "utm_content",
          "utm_term",
          "funnel_source",
        ]}
      />
      <FilterEditor
        filter={mapping.filter}
        onChange={(filter) => {
          const next = { ...mapping };
          if (filter) next.filter = filter;
          else delete next.filter;
          onChange(next);
        }}
        samples={samples}
        flatPaths={flatPaths}
      />
    </section>
  );
}

// --- Digistore24 special integration: gate/value logic lives in ------------
// ../lib/digistore-preview.ts (mirrors apps/api/src/lib/digistore.ts). ------

/**
 * Shown instead of the bucket editors when the Digistore24 preset is
 * active: explains the hardcoded rules and previews the decision +
 * computed values per sample so the operator can sanity-check them.
 */
function DigistorePanel({
  samples,
  onRemovePreset,
}: {
  samples: unknown[];
  onRemovePreset: () => void;
}) {
  const decisions = useMemo(
    () => samples.map((s) => ({ decision: digistoreShouldTrack(s), values: digistoreValues(s) })),
    [samples],
  );
  const trackCount = decisions.filter((d) => d.decision.track).length;
  const newest = decisions[0];
  return (
    <div className="mb-4 rounded-md border border-accent/40 bg-bg-primary p-3">
      <p className="mb-1 text-xs font-semibold text-text-primary">
        🔌 Digistore24 Special Integration aktiv
      </p>
      <ul className="mb-2 list-disc space-y-0.5 pl-4 text-xs text-text-secondary">
        <li>
          <span className="font-mono">deal_value</span> = Summe aller{" "}
          <span className="font-mono">product_netto_amount*</span> (Warenkorb
          inkl. Upsells) — bei gesetztem{" "}
          <span className="font-mono">voucher_code</span> stattdessen{" "}
          <span className="font-mono">amount_netto × Raten</span>, damit
          Rabatt-Käufer nicht als Vollzahler zählen
        </li>
        <li>
          <span className="font-mono">deal_cash_in</span> ={" "}
          <span className="font-mono">amount_netto</span> (jetzt gezahlte
          Netto-Summe)
        </li>
        <li>
          Getrackt wird nur <span className="font-mono">on_payment</span> mit{" "}
          <span className="font-mono">pay_sequence_no</span> fehlt/0/1 —
          Folgeraten &amp; Rebills werden verworfen
        </li>
        <li>
          Rückgaben (<span className="font-mono">on_refund</span>,{" "}
          <span className="font-mono">on_chargeback</span> bzw. Zapier-Typ{" "}
          <span className="font-mono">refund</span>/
          <span className="font-mono">chargeback</span>) werden als Event{" "}
          <span className="font-mono">Refund</span> mit negativem{" "}
          <span className="font-mono">deal_value</span> (Betrag der
          Rückbuchung) erfasst — an keine Werbeplattform
        </li>
        <li>
          Zapier-Format (kein <span className="font-mono">ipn_version</span>
          , verschachtelt): gegated über{" "}
          <span className="font-mono">transaction.type === "payment"</span>
        </li>
        <li>
          Im Zapier-Format bei{" "}
          <span className="font-mono">billing_type = single_payment</span>:{" "}
          <span className="font-mono">deal_value</span> = Summe der{" "}
          <span className="font-mono">product_txn_netto_amount</span> je
          Artikelplatz (tatsächlich berechnet, also nach Gutschein) statt{" "}
          <span className="font-mono">product_netto_amount</span> (Listenpreis).
          Bei Rate/Abo bleibt es beim Listenpreis — der ist dort der
          Gesamtauftragswert
        </li>
      </ul>
      {samples.length > 0 && (
        <p className="mb-2 font-mono text-[11px] text-text-secondary">
          {trackCount} von {samples.length} Samples würden getrackt
        </p>
      )}
      {newest && (
        <div className="mb-2 rounded-md bg-bg-tertiary p-2 font-mono text-[11px] leading-relaxed text-text-primary">
          <p className="mb-1 text-[10px] uppercase tracking-wider text-text-tertiary">
            Neuestes Sample
          </p>
          <p className={newest.decision.track ? "text-success" : "text-danger"}>
            {newest.decision.track ? "✓ würde getrackt" : "✗ würde verworfen"} —{" "}
            {newest.decision.reason}
          </p>
          {newest.decision.track && (
            <p>
              deal_value: {newest.values.deal_value ?? "—"} · cash_in:{" "}
              {newest.values.deal_cash_in ?? "—"} ·{" "}
              {newest.values.currency ?? ""} · Order:{" "}
              {newest.values.order_id ?? "—"} · {newest.values.email ?? "—"}
              {newest.values.voucher ? ` · Voucher: ${newest.values.voucher}` : ""}
            </p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onRemovePreset}
        className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-[11px] text-text-secondary hover:bg-bg-hover"
      >
        Special Integration entfernen → manuelles Mapping
      </button>
    </div>
  );
}

// --- Ablefy special integration: gate/value logic lives in -----------------
// ../lib/ablefy-preview.ts (mirrors apps/api/src/lib/ablefy.ts). -----------

/**
 * Shown instead of the bucket editors when the Ablefy preset is active:
 * explains the hardcoded Kauf-Event-Filter and previews the decision +
 * computed values per sample so the operator can sanity-check them.
 */
function AblefyPanel({
  samples,
  onRemovePreset,
}: {
  samples: unknown[];
  onRemovePreset: () => void;
}) {
  const decisions = useMemo(
    () => samples.map((s) => ({ decision: ablefyShouldTrack(s), values: ablefyValues(s) })),
    [samples],
  );
  const trackCount = decisions.filter((d) => d.decision.track).length;
  const newest = decisions[0];
  return (
    <div className="mb-4 rounded-md border border-accent/40 bg-bg-primary p-3">
      <p className="mb-1 text-xs font-semibold text-text-primary">
        🔌 Ablefy Special Integration aktiv
      </p>
      <ul className="mb-2 list-disc space-y-0.5 pl-4 text-xs text-text-secondary">
        <li>
          Getrackt werden nur die vier Kauf-Events:{" "}
          <span className="font-mono">order.one_time.paid</span>,{" "}
          <span className="font-mono">order.installment.paying</span>,{" "}
          <span className="font-mono">order.subscription.paying</span>,{" "}
          <span className="font-mono">order.limited_subscription.paying</span>{" "}
          — alle anderen (Kündigungen, Kursfortschritt, …) werden verworfen
        </li>
        <li>
          <span className="font-mono">order.*</span>-Events mit{" "}
          <span className="font-mono">refund</span> oder{" "}
          <span className="font-mono">chargeback</span> im Namen werden als{" "}
          <span className="font-mono">Refund</span> mit negativem{" "}
          <span className="font-mono">deal_value</span> erfasst (Annahme —
          Ablefy-Namen unbestätigt)
        </li>
        <li>
          <span className="font-mono">deal_value</span> ={" "}
          <span className="font-mono">order_amount_gross</span> (Gesamtwert der
          Bestellung)
        </li>
        <li>
          <span className="font-mono">deal_cash_in</span> ={" "}
          <span className="font-mono">payments_schedule[0].amount</span> (erste
          Rate — bei Einmalzahlung der Gesamtbetrag)
        </li>
      </ul>
      {samples.length > 0 && (
        <p className="mb-2 font-mono text-[11px] text-text-secondary">
          {trackCount} von {samples.length} Samples würden getrackt
        </p>
      )}
      {newest && (
        <div className="mb-2 rounded-md bg-bg-tertiary p-2 font-mono text-[11px] leading-relaxed text-text-primary">
          <p className="mb-1 text-[10px] uppercase tracking-wider text-text-tertiary">
            Neuestes Sample
          </p>
          <p className={newest.decision.track ? "text-success" : "text-danger"}>
            {newest.decision.track ? "✓ würde getrackt" : "✗ würde verworfen"} —{" "}
            {newest.decision.reason}
          </p>
          {newest.decision.track && (
            <p>
              deal_value: {newest.values.deal_value ?? "—"} · cash_in:{" "}
              {newest.values.deal_cash_in ?? "—"} ·{" "}
              {newest.values.currency ?? ""} · Order:{" "}
              {newest.values.order_id ?? "—"} · {newest.values.email ?? "—"}
              {newest.values.product_name ? ` · ${newest.values.product_name}` : ""}
            </p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onRemovePreset}
        className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-[11px] text-text-secondary hover:bg-bg-hover"
      >
        Special Integration entfernen → manuelles Mapping
      </button>
    </div>
  );
}

const FILTER_OP_OPTIONS: { value: FilterOp; label: string }[] = [
  { value: "equals", label: "=" },
  { value: "equals_or_missing", label: "= oder fehlt" },
  { value: "not_equals", label: "≠" },
  { value: "contains", label: "enthält" },
  { value: "exists", label: "vorhanden" },
  { value: "not_exists", label: "fehlt" },
];

// Mirrors passesFilter in apps/api/src/lib/webhook-mapping.ts — keep in sync.
function ruleMatches(payload: unknown, rule: MappingFilterRule): boolean {
  if (!rule.path) return true;
  const v = resolvePath(payload, rule.path)?.trim();
  const val = v === "" ? undefined : v;
  const want = String(rule.value ?? "").trim().toLowerCase();
  switch (rule.op) {
    case "exists":
      return val !== undefined;
    case "not_exists":
      return val === undefined;
    case "equals":
      return val !== undefined && val.toLowerCase() === want;
    case "equals_or_missing":
      return val === undefined || val.toLowerCase() === want;
    case "not_equals":
      return val === undefined || val.toLowerCase() !== want;
    case "contains":
      return val !== undefined && val.toLowerCase().includes(want);
    default:
      return true;
  }
}

/**
 * "Nur tracken wenn" — optional AND-combined rules gating ingestion.
 * The per-sample tally shows how many stored samples would pass, which is
 * exactly the signal for the Ratenzahlungs-case (erste vs. Folgezahlung).
 */
function FilterEditor({
  filter,
  onChange,
  samples,
  flatPaths,
}: {
  filter: WebhookMapping["filter"];
  onChange: (next: WebhookMapping["filter"]) => void;
  samples: unknown[];
  flatPaths: Map<string, string>;
}) {
  const rules = filter?.rules ?? [];
  const set = (next: MappingFilterRule[]) =>
    onChange(next.length > 0 ? { rules: next } : undefined);
  const update = (i: number, patch: Partial<MappingFilterRule>) =>
    set(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const passCount = useMemo(
    () => samples.filter((s) => rules.every((r) => ruleMatches(s, r))).length,
    [samples, rules],
  );

  return (
    <div className="mt-4 border-t border-border-primary pt-4">
      <p className="mb-1.5 text-xs font-medium text-text-primary">
        Nur tracken wenn (Filter)
      </p>
      <p className="mb-2 text-xs text-text-secondary">
        Alle Regeln müssen zutreffen, sonst wird der eingehende Webhook
        verworfen (Sample bleibt sichtbar). Typischer Fall: Ratenzahlung —
        nur die erste Zahlung tracken.
      </p>
      <ul className="space-y-1">
        {rules.map((rule, i) => (
          <li key={i} className="flex items-center gap-2 text-xs">
            <PathInput
              value={rule.path}
              onChange={(path) => update(i, { path })}
              flatPaths={flatPaths}
              placeholder="payment.number"
            />
            <select
              value={rule.op}
              onChange={(e) => update(i, { op: e.target.value as FilterOp })}
              className="w-24 flex-shrink-0 rounded-md border border-border-primary bg-bg-primary px-1 py-1 font-mono text-[11px] text-text-primary"
            >
              {FILTER_OP_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={rule.value ?? ""}
              onChange={(e) => update(i, { value: e.target.value })}
              disabled={rule.op === "exists" || rule.op === "not_exists"}
              placeholder="1"
              className="w-28 flex-shrink-0 rounded-md border border-border-primary bg-bg-primary px-2 py-1 font-mono text-[11px] text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none disabled:opacity-40"
            />
            <button
              type="button"
              onClick={() => set(rules.filter((_, idx) => idx !== i))}
              className="text-text-tertiary hover:text-danger"
              title="Regel entfernen"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={() => set([...rules, { path: "", op: "equals", value: "" }])}
          className="rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-[11px] text-text-secondary hover:bg-bg-hover"
        >
          + Regel
        </button>
        {rules.length > 0 && samples.length > 0 && (
          <span
            className={`font-mono text-[11px] ${
              passCount === 0 ? "text-danger" : "text-text-secondary"
            }`}
          >
            {passCount} von {samples.length} Samples würden getrackt
          </span>
        )}
      </div>
    </div>
  );
}

function BucketEditor({
  title,
  bucket,
  setBucket,
  samples,
  flatPaths,
  suggestedTargets,
}: {
  title: string;
  bucket: Record<string, string>;
  setBucket: (next: Record<string, string>) => void;
  samples: unknown[];
  flatPaths: Map<string, string>;
  suggestedTargets: string[];
}) {
  const [newTarget, setNewTarget] = useState("");
  const [newPath, setNewPath] = useState("");

  const remove = (target: string) => {
    const next = { ...bucket };
    delete next[target];
    setBucket(next);
  };
  const updatePath = (target: string, path: string) => {
    setBucket({ ...bucket, [target]: path });
  };
  const add = () => {
    if (!newTarget.trim() || !newPath.trim()) return;
    setBucket({ ...bucket, [newTarget.trim()]: newPath.trim() });
    setNewTarget("");
    setNewPath("");
  };

  return (
    <div className="mb-4 last:mb-0">
      <p className="mb-1.5 text-xs font-medium text-text-primary">{title}</p>
      <ul className="space-y-1">
        {Object.entries(bucket).map(([target, path]) => (
          <li key={target} className="flex items-center gap-2 text-xs">
            <span className="w-32 flex-shrink-0 font-mono text-text-tertiary">
              {target}
            </span>
            <span className="text-text-tertiary">←</span>
            <PathInput
              value={path}
              onChange={(p) => updatePath(target, p)}
              flatPaths={flatPaths}
            />
            <PreviewPill samples={samples} path={path} />
            <button
              type="button"
              onClick={() => remove(target)}
              className="text-text-tertiary hover:text-danger"
              title="Entfernen"
            >
              ×
            </button>
          </li>
        ))}
        <li className="flex items-center gap-2 text-xs">
          <select
            value={newTarget}
            onChange={(e) => setNewTarget(e.target.value)}
            className="w-32 flex-shrink-0 rounded-md border border-border-primary bg-bg-primary px-1 py-1 font-mono text-[11px] text-text-primary"
          >
            <option value="">+ Feld …</option>
            {suggestedTargets
              .filter((t) => !(t in bucket))
              .map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
          </select>
          <span className="text-text-tertiary">←</span>
          <PathInput
            value={newPath}
            onChange={setNewPath}
            flatPaths={flatPaths}
            placeholder="data.lead.email"
          />
          <button
            type="button"
            onClick={add}
            disabled={!newTarget.trim() || !newPath.trim()}
            className="rounded-md bg-accent px-2 py-1 text-[10px] font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-30"
          >
            +
          </button>
        </li>
      </ul>
    </div>
  );
}

/**
 * Flatten all samples into leaf-path → example-value. Newest sample wins;
 * empty values only fill gaps so the dropdown always shows something real.
 */
function flattenSamples(samples: unknown[]): Map<string, string> {
  const out = new Map<string, string>();
  const walk = (value: unknown, prefix: string) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((v, i) => walk(v, prefix ? `${prefix}.${i}` : `${i}`));
      return;
    }
    if (typeof value === "object") {
      for (const [k, v] of Object.entries(value)) {
        walk(v, prefix ? `${prefix}.${k}` : k);
      }
      return;
    }
    const s = String(value).trim();
    if (!out.has(prefix) || (out.get(prefix) === "" && s !== "")) {
      out.set(prefix, s);
    }
  };
  for (const sample of samples) walk(sample, "");
  return out;
}

/**
 * Rank all payload paths by closeness to the current one: siblings (same
 * parent object) first — that's where brutto/netto twins live — then paths
 * whose leaf shares a name token, then everything else in payload order.
 */
function rankAlternatives(
  flatPaths: Map<string, string>,
  current: string,
): { path: string; value: string }[] {
  const cur = current.trim();
  const cut = cur.lastIndexOf(".");
  const parent = cut === -1 ? "" : cur.slice(0, cut);
  const leaf = cur.slice(cut + 1).toLowerCase();
  const tokens = leaf.split(/[^a-z0-9]+/).filter((t) => t.length > 2);
  const scored: { path: string; value: string; score: number }[] = [];
  for (const [p, value] of flatPaths) {
    if (p === cur) continue;
    const pCut = p.lastIndexOf(".");
    const pParent = pCut === -1 ? "" : p.slice(0, pCut);
    const pLeaf = p.slice(pCut + 1).toLowerCase();
    let score = 0;
    if (pParent === parent) score += 2;
    if (tokens.some((t) => pLeaf.includes(t) || t.includes(pLeaf))) score += 3;
    scored.push({ path: p, value, score });
  }
  // Stable sort keeps payload order within equal scores.
  return scored.sort((a, b) => b.score - a.score);
}

/**
 * Path combobox: focus lists ALL leaf paths from the samples (scrollable,
 * example value beside each), typing filters them, and when the input
 * already holds a complete path (AI suggestion) the nearest alternatives
 * rank first — the brutto/netto case. Click swaps the path in place.
 */
function PathInput({
  value,
  onChange,
  flatPaths,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  flatPaths: Map<string, string>;
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);
  const { items, isRanked } = useMemo(() => {
    if (!focused) return { items: [], isRanked: false };
    const q = value.trim().toLowerCase();
    // Empty input or a complete known path → browse mode over all fields
    // (ranked by closeness when a path is set). Otherwise → search mode.
    if (!q || flatPaths.has(value.trim())) {
      return { items: rankAlternatives(flatPaths, value), isRanked: q !== "" };
    }
    const hits = [...flatPaths.entries()]
      .filter(([p]) => p.toLowerCase().includes(q))
      .map(([path, v]) => ({ path, value: v }))
      .sort(
        (a, b) =>
          a.path.toLowerCase().indexOf(q) - b.path.toLowerCase().indexOf(q),
      );
    return { items: hits, isRanked: false };
  }, [focused, flatPaths, value]);
  return (
    <div className="relative min-w-0 flex-1">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 font-mono text-[11px] text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
      />
      {items.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-y-auto rounded-md border border-border-primary bg-bg-primary shadow-lg">
          <li className="sticky top-0 bg-bg-primary px-2 py-1 text-[10px] uppercase tracking-wider text-text-tertiary">
            {isRanked
              ? `Alternativen aus den Samples (${items.length})`
              : `${items.length} ${items.length === 1 ? "Feld" : "Felder"} aus den Samples`}
          </li>
          {items.map((alt) => (
            <li key={alt.path}>
              <button
                type="button"
                // onMouseDown, damit der Klick vor dem Blur des Inputs greift
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(alt.path);
                  setFocused(false);
                }}
                className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-bg-hover"
              >
                <span className="flex-1 truncate font-mono text-[11px] text-text-primary">
                  {alt.path}
                </span>
                <span className="max-w-40 flex-shrink-0 truncate font-mono text-[10px] text-success">
                  {alt.value || "(leer)"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PreviewPill({
  samples,
  path,
}: {
  samples: unknown[];
  path: string;
}) {
  // Iterate samples newest → oldest and pick the first that resolves the
  // path to a non-empty value. A lot of webhook integrations have an empty
  // "ping" sample at the top — without this fallback the preview would
  // show "(nicht gefunden)" everywhere even though earlier samples have
  // the data.
  const { value, fromIndex } = useMemo(() => {
    for (let i = 0; i < samples.length; i++) {
      const v = resolvePath(samples[i], path);
      if (v !== undefined) return { value: v, fromIndex: i };
    }
    return { value: undefined, fromIndex: -1 };
  }, [samples, path]);

  if (value === undefined) {
    return (
      <span
        className="w-32 truncate text-right text-[10px] text-text-tertiary"
        title="Pfad in keinem der Samples gefunden"
      >
        (nicht gefunden)
      </span>
    );
  }
  const fromLabel =
    fromIndex === 0 ? "neuestes Sample" : `Sample ${fromIndex + 1}`;
  return (
    <span
      className="w-32 truncate text-right font-mono text-[10px] text-success"
      title={`${value}  ·  ${fromLabel}`}
    >
      {value}
    </span>
  );
}
